import React, { useCallback, useEffect, useMemo } from 'react';
import {
    Box,
    Collapse,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Translate } from 'c-translation';
import type { Campaign, PCADisplayType } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import type { TabWrapperSettings } from 'c-types';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useHistory, useLocation } from 'react-router-dom';
import type { UseBoolean } from 'react-hanger';
import { useAuthenticatedUser, useUrlState, useUrlStateValue } from 'c-hooks';
import JSURL from 'jsurl';
import { assocPath } from 'ramda';

import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import PCATabControls from 'c-main/Components/Campaign/CampaignOverviewDetailTabs/PCATabControls';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import BurstModeIcon from '@mui/icons-material/BurstMode';
import { Assessment, CreditCard, Insights, PhotoCamera } from '@mui/icons-material';

import { CalendarIcon } from '@mui/x-date-pickers-pro';
import { useAtomValue } from 'jotai';
import { Chip } from 'c-components';
import {
    CLASSIC_POSTINGS_METRIC,
    useCanSeeClassicPostings,
    useClassicPostings,
} from 'c-main/Components/Campaign/ClassicPostings';
import { COMBINED_REPORT_METRIC } from 'c-main/Components/Campaign/CombinedReport';
import TabsList from './TabsList';
import {
    costPerms,
    creativesPerms,
    displaysPerms,
    popImagePerms,
    reportsPerms,
    viewToFillPerms,
} from './constants';
import { atom_useLinearReportingOverride } from '../atoms';

interface TabsManagerProps {
    campaign: Campaign;
    id: number;
    digitalOpen: UseBoolean;
    classicOpen: UseBoolean;
    reportingOpen: UseBoolean;
}

const CONVERSION_MEASUREMENT_TEST_CAMPAIGN_IDS = [12907, 13191];
// Placeholder "Outcomes Measurement" tab (disabled, nothing behind it) shown to advertise the offering.
// Keyed by lowercased email; `null` means every campaign, otherwise only the listed campaign IDs.
const OUTCOMES_MEASUREMENT_PLACEHOLDER_USERS: Record<string, number[] | null> = {
    'sam.holland+openaius@uniledsoftware.com': null,
    'sam.holland+talonus@uniledsoftware.com': [10630],
};

const TabsManager: React.FC<TabsManagerProps> = ({
    campaign,
    id,
    reportingOpen,
    digitalOpen,
    classicOpen,
}) => {
    const isModifiedEvent = (e: React.MouseEvent<HTMLElement>) =>
        e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0;

    const showViewToFill = campaign?.view_to_fill === 1 && campaign?.view_to_fill_url !== null;
    const viewToFillUrl = campaign?.view_to_fill_url;

    const { hasPCAData, environmentMode } = useCampaignWrapperState(id);
    const { hasAll } = useUserPermissions();
    const authedUser = useAuthenticatedUser();
    const outcomesPlaceholderCampaignIds =
        OUTCOMES_MEASUREMENT_PLACEHOLDER_USERS[authedUser?.user?.email?.toLowerCase() ?? ''];
    const showOutcomesManagement =
        outcomesPlaceholderCampaignIds === null ||
        (outcomesPlaceholderCampaignIds?.includes(id) ?? false);
    const history = useHistory();
    const location = useLocation();

    const { push, pushMany, replaceMany } = useUrlState();
    const currentType = useUrlStateValue<'digital' | 'classic' | null>('type', null);
    const currentMetric = useUrlStateValue<string | null>('metric', null);
    const currentConversionTabRaw = useUrlStateValue<string>('cmTab', 'spend');
    const currentConversionDetailTabRaw = useUrlStateValue<string | null>('cmConversionsTab', null);

    const availableTypes = useMemo<ReadonlyArray<PCADisplayType>>(
        () => (environmentMode ? campaign?.pca_display_types?.[environmentMode] ?? [] : []),
        [campaign?.pca_display_types, environmentMode],
    );
    const hasKnownDisplayTypesForEnvironment = useMemo(
        () =>
            Boolean(
                environmentMode &&
                    campaign?.pca_display_types &&
                    Object.prototype.hasOwnProperty.call(
                        campaign.pca_display_types,
                        environmentMode,
                    ),
            ),
        [campaign?.pca_display_types, environmentMode],
    );

    const hasDigitalRaw = availableTypes.includes('Display');
    const hasClassic = availableTypes.includes('Classic');
    const showConversionMeasurementTest = CONVERSION_MEASUREMENT_TEST_CAMPAIGN_IDS.includes(id);
    const hasDigital =
        hasDigitalRaw || (hasKnownDisplayTypesForEnvironment && availableTypes.length === 0);

    // Keyed off `type` to agree with CampaignReportingWrapper -- when the two disagree they fight
    // over `?type=` in the URL. Every DSP type, `vistar_us2` included, reports as DSP.
    const isDspCampaignType = campaign.type !== 'linear' && campaign.type !== 'default';
    const campaignHasDspReporting = useMemo(
        () => isDspCampaignType || id === 9464 || id === 10160 || id === 8896,
        [isDspCampaignType, id],
    );
    const useLinearReportingOverride = useAtomValue(atom_useLinearReportingOverride);

    const canSeeClassicPostings = useCanSeeClassicPostings(campaign);
    const { outOfWindow: classicPostingsOutOfWindow } = useClassicPostings(
        id,
        canSeeClassicPostings,
    );
    const isDspReporting = campaignHasDspReporting && !useLinearReportingOverride;

    const selectedTab = useMemo(() => {
        const match = location.pathname.match(/\/campaigns\/\d+\/(\w+)/);
        return match?.[1] ?? null;
    }, [location.pathname]);

    const conversionChildren = useMemo(
        () => [
            { key: 'spend', label: 'Spend' },
            { key: 'exposure', label: 'Exposure' },
            { key: 'views', label: 'Views' },
            { key: 'initiate_checkout', label: 'Initiate Checkout' },
            { key: 'purchase', label: 'Purchase' },
        ],
        [],
    );

    const conversionChildKeys = useMemo(
        () => new Set(conversionChildren.map(c => c.key)),
        [conversionChildren],
    );
    const currentConversionTab = useMemo(
        () =>
            currentConversionTabRaw && conversionChildKeys.has(currentConversionTabRaw)
                ? currentConversionTabRaw
                : currentConversionDetailTabRaw &&
                  conversionChildKeys.has(currentConversionDetailTabRaw)
                ? currentConversionDetailTabRaw
                : 'spend',
        [currentConversionTabRaw, currentConversionDetailTabRaw, conversionChildKeys],
    );

    const classicChildren = useMemo(
        () => [
            { key: 'summary', label: 'Summary' },
            { key: 'display', label: 'Display Days' },
            { key: 'impacts', label: 'Impacts' },
        ],
        [],
    );

    const reportingChildren = useMemo(() => {
        const kids = [];

        // Both sit directly under Digital / Classic, behind the same per-campaign flag.
        if (canSeeClassicPostings) {
            kids.push({
                key: COMBINED_REPORT_METRIC,
                label: <Translate path="Modules.Main.Campaigns.Overview.Tabs.combined" />,
                permissions: reportsPerms,
            });
            // The amber count is lines out of window.
            kids.push({
                key: CLASSIC_POSTINGS_METRIC,
                label: (
                    <Stack direction="row" alignItems="center" gap={1} component="span">
                        <Translate path="Modules.Main.Campaigns.Overview.Tabs.classicPostings" />
                        {classicPostingsOutOfWindow > 0 && (
                            <Chip size="small" color="warning" label={classicPostingsOutOfWindow} />
                        )}
                    </Stack>
                ),
                permissions: reportsPerms,
            });
        }

        if (campaign?.happydemics_id != null) {
            kids.push({
                key: 'brandUpliftStudy',
                label: <Translate path="Modules.Main.Campaigns.Overview.HappyDemics.title" />,
                permissions: reportsPerms,
            });
        }

        return kids;
    }, [campaign?.happydemics_id, canSeeClassicPostings, classicPostingsOutOfWindow]);

    const filteredReportingChildren = useMemo(
        () => reportingChildren.filter(c => hasAll(c.permissions)),
        [reportingChildren, hasAll],
    );

    const reportingChildKeys = useMemo(
        () => new Set(filteredReportingChildren.map(c => c.key)),
        [filteredReportingChildren],
    );

    const isReportingLeafSelected =
        !currentType && !!currentMetric && reportingChildKeys.has(currentMetric);

    const digitalChildren = useMemo(() => {
        const base = [
            {
                key: 'summary',
                label: (
                    <Translate path="Modules.Main.Campaigns.Overview.Tabs.analyticsSummaryReports" />
                ),
            },
            {
                key: 'impacts',
                label: <Translate path="Modules.Main.Campaigns.Overview.Tabs.impactsReports" />,
            },
            {
                key: 'plays',
                label:
                    currentType === 'classic' ? (
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.displayDaysTitle" />
                    ) : (
                        <Translate path="Modules.Main.Campaigns.Overview.Tabs.playsReports" />
                    ),
            },
            {
                key: 'time',
                label: <Translate path="Modules.Main.Campaigns.Overview.Tabs.timeReports" />,
            },
        ] as const;

        const extra: Array<{ key: string; label: React.ReactNode }> = [];
        if (campaign.energy_efficiency_enabled) {
            extra.push({
                key: 'energy',
                label: <Translate path="Modules.Main.Campaigns.Overview.Tabs.energyUsage" />,
            });
        }
        if (campaign.display_verification_enabled) {
            extra.push({
                key: 'display_verf',
                label: (
                    <Translate path="Modules.Main.Campaigns.Overview.Tabs.displayVerification" />
                ),
            });
        }
        if (!isDspReporting && campaignHasDspReporting) {
            extra.push({
                key: 'dsp',
                label: <Translate path="Modules.Main.Campaigns.Overview.Tabs.dsp" />,
            });
        }
        return [...base, ...extra];
    }, [campaign, campaignHasDspReporting, currentType, isDspReporting]);

    const dspChildren = useMemo(
        () => [
            { key: 'summary', label: 'Summary' },
            { key: 'analysis', label: 'Analysis' },
        ],
        [],
    );

    const classicKeys = useMemo(() => new Set(classicChildren.map(c => c.key)), [classicChildren]);
    const digitalKeys = useMemo(() => new Set(digitalChildren.map(d => d.key)), [digitalChildren]);
    useEffect(() => {
        if (isDspReporting) return;
        if (selectedTab !== 'reporting') return;

        if (isReportingLeafSelected) {
            reportingOpen.setTrue();
            digitalOpen.setFalse();
            classicOpen.setFalse();
            return;
        }

        const defaultType: 'digital' | 'classic' | null = hasDigital
            ? 'digital'
            : hasClassic
            ? 'classic'
            : null;

        let nextType = currentType;
        if (
            !nextType ||
            (nextType === 'digital' && !hasDigital) ||
            (nextType === 'classic' && !hasClassic)
        ) {
            nextType = defaultType;
        }

        if (!nextType) return;

        const validKeys = nextType === 'digital' ? digitalKeys : classicKeys;
        const defaultMetric = 'summary';
        const nextMetric =
            currentMetric && validKeys.has(currentMetric) ? currentMetric : defaultMetric;

        // Filling in / correcting the URL, not a navigation: replace, or Back loops straight back here.
        const urlFixes: Record<string, string> = {};
        if (nextType !== currentType) urlFixes.type = nextType;
        if (nextMetric !== currentMetric) urlFixes.metric = nextMetric;
        if (Object.keys(urlFixes).length > 0) replaceMany(urlFixes);

        reportingOpen.setTrue();
        if (nextType === 'digital') {
            digitalOpen.setTrue();
            classicOpen.setFalse();
        } else {
            classicOpen.setTrue();
            digitalOpen.setFalse();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isDspReporting,
        selectedTab,
        currentType,
        currentMetric,
        hasDigital,
        hasClassic,
        digitalKeys,
        classicKeys,
        isReportingLeafSelected,
    ]);

    useEffect(() => {
        if (selectedTab === 'conversionmeasurementtest1234') {
            reportingOpen.setFalse();
            digitalOpen.setFalse();
            classicOpen.setFalse();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    useEffect(() => {
        if (isDspReporting) return;
        if (selectedTab !== 'reporting') return;

        if (isReportingLeafSelected) {
            reportingOpen.setTrue();
            digitalOpen.setFalse();
            classicOpen.setFalse();
            return;
        }

        if (currentType === 'digital') {
            reportingOpen.setTrue();
            digitalOpen.setTrue();
            classicOpen.setFalse();
        } else if (currentType === 'classic') {
            reportingOpen.setTrue();
            classicOpen.setTrue();
            digitalOpen.setFalse();
        }

        // Correcting an unavailable type, not a navigation: replace, or Back loops straight back here.
        if (currentType === 'digital' && !hasDigital) {
            replaceMany(
                hasClassic ? { type: 'classic', metric: 'summary' } : { type: null, metric: null },
            );
        }
        if (currentType === 'classic' && !hasClassic) {
            replaceMany(
                hasDigital ? { type: 'digital', metric: 'summary' } : { type: null, metric: null },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab, currentType, hasDigital, hasClassic, isReportingLeafSelected]);

    const tabs = useMemo<TabWrapperSettings['tabs']>(() => {
        const theTabs = [
            {
                title: (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <DisplaySettingsIcon color="primary" />
                        <Translate path="Modules.Main.Campaigns.Overview.Tabs.displays" />
                    </Stack>
                ),
                permissions: displaysPerms,
                urlName: 'displays',
                className: 'campaign-tab-displays',
            },
            {
                title: (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <BurstModeIcon color="primary" />
                        <Translate path="Modules.Main.Campaigns.Overview.Tabs.creatives" />
                    </Stack>
                ),
                permissions: creativesPerms,
                urlName: 'creatives',
                className: 'campaign-tab-creatives',
            },
            {
                title: (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Assessment color="primary" />
                        <PCATabControls
                            id={id}
                            showTypeToggle={hasPCAData}
                            isOpen={reportingOpen.value}
                            onToggle={reportingOpen.toggle}
                            showSettings
                        />
                    </Stack>
                ),
                permissions: reportsPerms,
                urlName: 'reporting',
                className: 'campaign-tab-reporting',
            },
            ...(showOutcomesManagement
                ? [
                      {
                          title: (
                              <Stack direction="row" alignItems="center" gap={1}>
                                  <Insights color="primary" />
                                  <Typography>Outcomes Measurement</Typography>
                              </Stack>
                          ),
                          urlName: 'outcomes',
                          className: 'campaign-tab-outcomes',
                          disabled: true,
                      },
                  ]
                : []),
            ...(showViewToFill
                ? [
                      {
                          title: (
                              <Stack direction="row" alignItems="center" gap={1}>
                                  <PhotoCamera color="primary" />
                                  <Translate path="Modules.Main.Campaigns.Overview.Tabs.viewToFill" />
                              </Stack>
                          ),
                          permissions: viewToFillPerms,
                          urlName: 'viewToFill',
                          className: 'campaign-tab-viewtofill',
                      },
                  ]
                : [
                      {
                          title: (
                              <Stack direction="row" alignItems="center" gap={1}>
                                  <PhotoCamera color="primary" />
                                  <Translate path="Modules.Main.Campaigns.Overview.Tabs.popImages" />
                              </Stack>
                          ),
                          permissions: popImagePerms,
                          urlName: 'popImages',
                          className: 'campaign-tab-popimages',
                      },
                  ]),
            {
                title: (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <CalendarIcon color="primary" />
                        <Translate path="Modules.Main.Campaigns.Overview.ActionLabels.campaignSchedule" />
                    </Stack>
                ),
                permissions: [
                    PermissionName.UniliveapiSchedulesRead,
                    PermissionName.UniliveapiRulesRead,
                    PermissionName.DisplaysLine_itemsRead,
                    PermissionName.DisplaysScreensRead,
                ],
                urlName: 'schedule',
                className: 'campaign-tab-schedule',
            },
        ];

        if (showConversionMeasurementTest) {
            theTabs.splice(4, 0, {
                title: (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Assessment color="primary" />
                        <PCATabControls
                            id={id}
                            showSettings={false}
                            showStatusIcons={false}
                            alwaysShowTitle
                            title={<Typography>Conversion Measurement</Typography>}
                        />
                    </Stack>
                ),
                permissions: reportsPerms,
                urlName: 'conversionmeasurementtest1234',
                className: 'campaign-tab-conversionmeasurementtest1234',
            });
        }

        if (campaign?.budgetSummary != null) {
            const popImagesIdx = theTabs.findIndex(
                tab => tab.urlName === 'popImages' || tab.urlName === 'viewToFill',
            );
            const costsIdx = popImagesIdx >= 0 ? popImagesIdx + 1 : 4;
            theTabs.splice(costsIdx, 0, {
                title: (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <CreditCard color="primary" />
                        <Translate path="Modules.Main.Campaigns.Overview.Tabs.cost" />
                    </Stack>
                ),
                permissions: costPerms,
                urlName: 'costs',
                className: 'campaign-tab-costs',
            });
        }

        return theTabs;
    }, [
        hasPCAData,
        id,
        reportingOpen.value,
        reportingOpen.toggle,
        campaign,
        showConversionMeasurementTest,
        showOutcomesManagement,
        showViewToFill,
    ]);

    const getCurrentUrlState = useCallback(
        () =>
            JSURL.tryParse(
                new URLSearchParams(window.location.search).get('state') ?? {},
                {},
            ) as Record<string, unknown>,
        [],
    );

    const stripConversionState = useCallback((state: Record<string, unknown>) => {
        const nextState = { ...state };
        delete nextState.cmTab;
        delete nextState.cmConversionsTab;
        return nextState;
    }, []);

    const ensureTypeAndMetric = (type: 'digital' | 'classic', metric?: string) => {
        if (isDspReporting) return;
        if ((type === 'digital' && !hasDigital) || (type === 'classic' && !hasClassic)) return;

        const defaultMetric = 'summary';
        const currentValid =
            type === 'digital'
                ? digitalKeys.has(currentMetric ?? '')
                : classicKeys.has(currentMetric ?? '');
        const nextMetric = metric ?? (currentValid ? currentMetric : defaultMetric);

        reportingOpen.setTrue();
        if (type === 'digital') {
            digitalOpen.setTrue();
            classicOpen.setFalse();
        } else {
            classicOpen.setTrue();
            digitalOpen.setFalse();
        }

        const nextHref = buildReportingHref({ type, metric: nextMetric });
        if (selectedTab === 'conversionmeasurementtest1234') {
            window.location.assign(nextHref);
            return;
        }
        history.push(nextHref);
    };

    const filteredTabs = useMemo(() => tabs.filter(tab => hasAll(tab.permissions)), [tabs, hasAll]);

    const isDigitalOpen = reportingOpen.value && digitalOpen.value;
    const isClassicOpen = reportingOpen.value && classicOpen.value;
    const activeSelection = useMemo(() => {
        if (isDspReporting) {
            const hasChild =
                selectedTab === 'reporting' &&
                reportingOpen.value &&
                !!currentMetric &&
                dspChildren.some(c => c.key === currentMetric);

            return {
                reporting: selectedTab === 'reporting' && !hasChild,
                parent: null as 'digital' | 'classic' | null,
                child:
                    hasChild && currentMetric
                        ? ({ type: 'digital', key: currentMetric } as {
                              type: 'digital' | 'classic';
                              key: string;
                          })
                        : null,
            };
        }

        if (selectedTab !== 'reporting') {
            return {
                reporting: false,
                parent: null as 'digital' | 'classic' | null,
                child: null as null | { type: 'digital' | 'classic'; key: string },
                leaf: null as null | string,
            };
        }

        if (!reportingOpen.value) {
            return { reporting: true, parent: null, child: null, leaf: null };
        }

        if (isReportingLeafSelected && currentMetric) {
            return { reporting: false, parent: null, child: null, leaf: currentMetric };
        }

        const openDigital = reportingOpen.value && digitalOpen.value;
        const openClassic = reportingOpen.value && classicOpen.value;

        if (
            currentType &&
            ((currentType === 'digital' && openDigital) ||
                (currentType === 'classic' && openClassic)) &&
            currentMetric
        ) {
            return {
                reporting: false,
                parent: null,
                child: { type: currentType, key: currentMetric },
                leaf: null,
            };
        }

        if (currentType) {
            return { reporting: false, parent: currentType, child: null, leaf: null };
        }

        if (openDigital)
            return { reporting: false, parent: 'digital' as const, child: null, leaf: null };
        if (openClassic)
            return { reporting: false, parent: 'classic' as const, child: null, leaf: null };
        if (hasDigital)
            return { reporting: false, parent: 'digital' as const, child: null, leaf: null };
        if (hasClassic)
            return { reporting: false, parent: 'classic' as const, child: null, leaf: null };

        return { reporting: true, parent: null, child: null, leaf: null };
    }, [
        isDspReporting,
        selectedTab,
        reportingOpen.value,
        digitalOpen.value,
        classicOpen.value,
        currentType,
        currentMetric,
        hasDigital,
        hasClassic,
        dspChildren,
        isReportingLeafSelected,
    ]);

    const isChildSelected = (type: 'digital' | 'classic', key: string) =>
        activeSelection.child?.type === type && activeSelection.child.key === key;

    const buildReportingHref = useCallback(
        (opts?: { type?: 'digital' | 'classic' | null; metric?: string | null }) => {
            const currentUrlState = getCurrentUrlState();

            const resolvedType =
                opts?.type !== undefined
                    ? opts.type
                    : currentType &&
                      ((currentType === 'digital' && hasDigital) ||
                          (currentType === 'classic' && hasClassic))
                    ? currentType
                    : hasDigital
                    ? 'digital'
                    : hasClassic
                    ? 'classic'
                    : null;

            const typeKeys = resolvedType === 'digital' ? digitalKeys : classicKeys;
            const resolvedMetric =
                opts?.metric !== undefined
                    ? opts.metric
                    : resolvedType && currentMetric && typeKeys.has(currentMetric)
                    ? currentMetric
                    : resolvedType
                    ? 'summary'
                    : null;

            let nextUrlState = stripConversionState(currentUrlState);
            if (environmentMode != null && environmentMode !== '') {
                nextUrlState = assocPath(['environment'], environmentMode, nextUrlState);
            }
            nextUrlState = assocPath(['type'], resolvedType, nextUrlState);
            nextUrlState = assocPath(['metric'], resolvedMetric, nextUrlState);

            return `/campaigns/${id}/reporting?state=${JSURL.stringify(nextUrlState)}`;
        },
        [
            classicKeys,
            currentMetric,
            currentType,
            digitalKeys,
            environmentMode,
            getCurrentUrlState,
            hasClassic,
            hasDigital,
            id,
            stripConversionState,
        ],
    );

    const buildCampaignTabHref = useCallback(
        (tabName: string) => {
            if (tabName === 'viewToFill') return viewToFillUrl;
            if (tabName === 'reporting') return buildReportingHref();
            if (tabName === 'conversionmeasurementtest1234')
                return `/campaigns/${id}/conversionmeasurementtest1234`;
            return `/campaigns/${id}/${tabName}`;
        },
        [buildReportingHref, id, viewToFillUrl],
    );

    const selectedTabForTabsList =
        selectedTab === 'reporting' &&
        (activeSelection.leaf || activeSelection.child || activeSelection.parent)
            ? null
            : selectedTab;

    return (
        <TabsList
            filteredTabs={filteredTabs}
            selectedTab={selectedTabForTabsList}
            getTabHref={buildCampaignTabHref}
            onTabClick={(tabName, e) => {
                if (isModifiedEvent(e)) return;
                e.preventDefault();

                if (tabName === 'viewToFill') {
                    window.open(viewToFillUrl, '_blank', 'noopener,noreferrer');
                    return;
                }

                if (tabName !== 'reporting') {
                    if (tabName === 'conversionmeasurementtest1234') {
                        if (currentType) push('type', null);
                        if (currentMetric) push('metric', null);
                        reportingOpen.setFalse();
                        digitalOpen.setFalse();
                        classicOpen.setFalse();
                        push('cmTab', currentConversionTab);
                        history.push({
                            pathname: `/campaigns/${id}/conversionmeasurementtest1234`,
                            search: window.location.search,
                        });
                        return;
                    }
                    if (currentType) push('type', null);
                    if (!isDspReporting && currentMetric) push('metric', null);
                    digitalOpen.setFalse();
                    classicOpen.setFalse();
                    history.push(`/campaigns/${id}/${tabName}`);
                    return;
                }

                if (isDspReporting) {
                    if (currentType) push('type', null);
                    if (!currentMetric) push('metric', 'summary');
                    reportingOpen.setTrue();
                    history.push(`/campaigns/${id}/reporting`);
                    return;
                }

                if (!currentType) {
                    if (hasDigital) {
                        ensureTypeAndMetric('digital', 'summary');
                    } else if (hasClassic) {
                        ensureTypeAndMetric('classic', 'summary');
                    } else {
                        const nextHref = buildReportingHref();
                        if (selectedTab === 'conversionmeasurementtest1234') {
                            window.location.assign(nextHref);
                            return;
                        }
                        history.push(nextHref);
                    }
                } else {
                    const nextHref = buildReportingHref();
                    if (selectedTab === 'conversionmeasurementtest1234') {
                        window.location.assign(nextHref);
                        return;
                    }
                    history.push(nextHref);
                }
            }}
            renderAfterTab={tab => {
                if (tab.urlName !== 'reporting') return null;

                if (isDspReporting) {
                    return (
                        <Collapse in={reportingOpen.value} timeout="auto" unmountOnExit>
                            <Box pl={2}>
                                {dspChildren.map(child => (
                                    <ListItem
                                        key={`dsp-${child.key}`}
                                        disablePadding
                                        onMouseDown={e => e.stopPropagation()}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <ListItemButton
                                            selected={
                                                selectedTab === 'reporting' &&
                                                currentMetric === child.key
                                            }
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (currentType) push('type', null);
                                                push('metric', child.key);
                                            }}
                                        >
                                            <ListItemText primary={child.label} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </Box>
                        </Collapse>
                    );
                }

                if (!hasPCAData) return null;

                return (
                    <Collapse in={reportingOpen.value} timeout="auto" unmountOnExit>
                        <Box pl={2} pr={2} pb={1}>
                            <Stack>
                                {hasDigital && (
                                    <ListItem
                                        disablePadding
                                        onMouseDown={e => e.stopPropagation()}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <ListItemButton
                                            component="a"
                                            href={buildReportingHref({ type: 'digital' })}
                                            selected={activeSelection.parent === 'digital'}
                                            onClick={e => {
                                                if (isModifiedEvent(e)) return;
                                                e.stopPropagation();
                                                e.preventDefault();
                                                ensureTypeAndMetric('digital');
                                            }}
                                            aria-expanded={isDigitalOpen ? 'true' : 'false'}
                                            sx={{ gap: 1 }}
                                        >
                                            <ListItemText primary="Digital" />
                                            <ExpandMoreIcon
                                                fontSize="small"
                                                sx={{
                                                    ml: 'auto',
                                                    transform: isDigitalOpen
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                    transition: 'transform .2s',
                                                }}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    digitalOpen.toggle();
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                )}

                                {hasDigital && (
                                    <Collapse in={isDigitalOpen} timeout="auto" unmountOnExit>
                                        <Box pl={2}>
                                            {digitalChildren.map(child => (
                                                <ListItem
                                                    key={`digital-${child.key}`}
                                                    disablePadding
                                                    onMouseDown={e => e.stopPropagation()}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <ListItemButton
                                                        component="a"
                                                        href={buildReportingHref({
                                                            type: 'digital',
                                                            metric: child.key,
                                                        })}
                                                        selected={isChildSelected(
                                                            'digital',
                                                            child.key,
                                                        )}
                                                        onClick={e => {
                                                            if (isModifiedEvent(e)) return;
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            ensureTypeAndMetric(
                                                                'digital',
                                                                child.key,
                                                            );
                                                        }}
                                                    >
                                                        <ListItemText primary={child.label} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </Box>
                                    </Collapse>
                                )}

                                {hasClassic && (
                                    <ListItem
                                        disablePadding
                                        onMouseDown={e => e.stopPropagation()}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <ListItemButton
                                            component="a"
                                            href={buildReportingHref({ type: 'classic' })}
                                            selected={activeSelection.parent === 'classic'}
                                            onClick={e => {
                                                if (isModifiedEvent(e)) return;
                                                e.stopPropagation();
                                                e.preventDefault();
                                                ensureTypeAndMetric('classic');
                                            }}
                                            aria-expanded={isClassicOpen ? 'true' : 'false'}
                                            sx={{ gap: 1 }}
                                        >
                                            <ListItemText primary="Classic" />
                                            <ExpandMoreIcon
                                                fontSize="small"
                                                sx={{
                                                    ml: 'auto',
                                                    transform: isClassicOpen
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                    transition: 'transform .2s',
                                                }}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    classicOpen.toggle();
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                )}

                                {hasClassic && (
                                    <Collapse in={isClassicOpen} timeout="auto" unmountOnExit>
                                        <Box pl={2}>
                                            {classicChildren.map(child => (
                                                <ListItem
                                                    key={`classic-${child.key}`}
                                                    disablePadding
                                                    onMouseDown={e => e.stopPropagation()}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <ListItemButton
                                                        component="a"
                                                        href={buildReportingHref({
                                                            type: 'classic',
                                                            metric: child.key,
                                                        })}
                                                        selected={isChildSelected(
                                                            'classic',
                                                            child.key,
                                                        )}
                                                        onClick={e => {
                                                            if (isModifiedEvent(e)) return;
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            ensureTypeAndMetric(
                                                                'classic',
                                                                child.key,
                                                            );
                                                        }}
                                                    >
                                                        <ListItemText primary={child.label} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </Box>
                                    </Collapse>
                                )}
                                {filteredReportingChildren.map(child => (
                                    <ListItem
                                        key={`reporting-${child.key}`}
                                        disablePadding
                                        onMouseDown={e => e.stopPropagation()}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <ListItemButton
                                            component="a"
                                            href={buildReportingHref({
                                                type: null,
                                                metric: child.key,
                                            })}
                                            selected={activeSelection.leaf === child.key}
                                            onClick={e => {
                                                if (isModifiedEvent(e)) return;
                                                e.stopPropagation();
                                                e.preventDefault();

                                                if (currentType) push('type', null);
                                                push('metric', child.key);

                                                reportingOpen.setTrue();
                                                digitalOpen.setFalse();
                                                classicOpen.setFalse();

                                                setTimeout(() => {
                                                    history.push({
                                                        pathname: `/campaigns/${id}/reporting`,
                                                        search: window.location.search,
                                                    });
                                                }, 0);
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <IfHasAllPermissions
                                                        permissions={child.permissions}
                                                    >
                                                        {child.label}
                                                    </IfHasAllPermissions>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                                {id === 11911 && (
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() =>
                                                window.open(
                                                    'https://console.kochava.com/',
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }
                                        >
                                            <ListItemText>Conversion Measurement</ListItemText>
                                        </ListItemButton>
                                    </ListItem>
                                )}
                            </Stack>
                        </Box>
                    </Collapse>
                );
            }}
        />
    );
};

export default TabsManager;
